import React from 'react';
import {
  Alert, Badge, Button, ConfirmDialog, Drawer, DrawerBody, DrawerFooter, DrawerHeader,
  EmptyState, ErrorState, Skeleton, SkeletonRegion, SkeletonText, Spinner, Textarea,
  phone, useToast,
} from '@parsina/framework';
import {
  ArrowUpLeft, CalendarClock, CalendarPlus, CircleCheck, ExternalLink, Mail, MessageCircle,
  MessageSquare, Phone, PhoneCall, SearchX, StickyNote, UserCheck, UserMinus, Users, XCircle,
} from 'lucide-react';

import { useTranslation, LTR_TEXT, MIRROR_ICON } from '../../i18n';
import { useShortcut } from '../../lib/shortcuts';
import { EMPTY } from '../../lib/format';
import {
  announceDemoRequestsChanged, crmErrorMessage, crmGql,
} from '../../lib/crmApi';
import {
  buildTimeline, contactsOf, detailRows, firstResponseMs, followUpsOfLead, isDoctorPageLead,
  isOverdue, offeredMoves, otherRequesters, pageRequestRows, safeHttpUrl, utmPairs,
} from '../../lib/demoRequestsView';
import { PageLink } from './DemoRequestsTable';
import {
  NOTE_MAX, channelKey, isClosedStatus, leadStatusKey, outcomeKey,
} from '../../constants/leadPipeline';
import {
  ADD_NOTE, CUSTOMER_DEMO_LEADS_QUERY, DEMO_LEAD_CONTEXT_QUERY, DEMO_LEAD_QUERY,
  FIND_BY_CONTACT_QUERY, SET_FOLLOW_UP_STATUS, SET_LEAD_STATUS, UPDATE_LEAD,
} from './queries';
import {
  Code, CopyButton, Fact, LeadStatusBadge, OwnerLabel, Section, ShortRef, useVocab,
} from './shared';
import LogContactDialog from './LogContactDialog';
import FollowUpDialog from './FollowUpDialog';
import LostDialog from './LostDialog';

/**
 * One demo request, beside the queue (§3.2 T2–T10). Deep-linkable as
 * `?lead=<uuid>` — the lead's id is the only identifier that reaches the URL.
 *
 * It loads on its own (never from the list page, so a deep link to a lead on
 * another page or tab still opens), and it REFETCHES ON WINDOW FOCUS: two reps
 * can open the same request, the owner is last-write-wins (§3.2 T3), and the
 * current owner is shown prominently rather than the one from ten minutes ago.
 *
 * The reverse lookup «other requests with this number» is audited by the CRM
 * on every call, so it runs ONCE per drawer open, not on refetch.
 *
 * A closed lead (won/lost) renders read-only: the service refuses every edit.
 */

const CHANNEL_ICON = {
  call: PhoneCall, whatsapp: MessageCircle, sms: MessageSquare, email: Mail, in_person: Users,
};

/** A duration in the largest unit that reads naturally: minutes, hours, then days. */
export function spanLabel(ms, t) {
  const minutes = Math.max(1, Math.round(ms / 60_000));
  if (minutes < 60) return t('demoRequests.span.minutes', { count: minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 48) return t('demoRequests.span.hours', { count: hours });
  return t('demoRequests.span.days', { count: Math.round(hours / 24) });
}

function useLeadDetail(leadId) {
  const [state, setState] = React.useState({ status: 'loading' });
  const [refreshing, setRefreshing] = React.useState(false);
  const [refreshFailed, setRefreshFailed] = React.useState(false);
  const seq = React.useRef(0);

  const load = React.useCallback(async ({ quiet = false } = {}) => {
    seq.current += 1;
    const mine = seq.current;
    if (quiet) setRefreshing(true); else setState({ status: 'loading' });
    try {
      const d = await crmGql(DEMO_LEAD_QUERY, { id: leadId });
      const lead = d?.crmLead;
      if (!lead) {
        if (mine === seq.current) setState({ status: 'missing' });
        return;
      }
      const c = await crmGql(DEMO_LEAD_CONTEXT_QUERY, { customerId: lead.customerId, leadId: lead.id });
      if (mine !== seq.current) return;
      setRefreshFailed(false);
      setState({
        status: 'ready',
        lead,
        customer: c?.crmCustomer || null,
        interactions: c?.crmInteractions?.interactions || [],
        followUps: followUpsOfLead(c?.crmFollowUps?.followUps, lead.id),
        notes: c?.crmNotes?.notes || [],
      });
    } catch (err) {
      if (mine !== seq.current) return;
      if (err?.code === 'NOT_FOUND') { setState({ status: 'missing' }); return; }
      // A failed background refresh keeps what is on screen and says so; only
      // a failed FIRST load replaces the body with the error state.
      if (quiet) setRefreshFailed(true);
      else setState({ status: 'error', error: err });
    } finally {
      if (mine === seq.current) setRefreshing(false);
    }
  }, [leadId]);

  React.useEffect(() => { load(); }, [load]);

  return { ...state, refreshing, refreshFailed, reload: () => load({ quiet: true }), retry: () => load() };
}

/** «Other requests from this number» — one audited lookup per drawer open. */
function useSameNumber(leadId, customerId, contact, intent) {
  const [state, setState] = React.useState({ status: 'idle', items: [] });
  const done = React.useRef(null);

  React.useEffect(() => {
    if (!customerId || !contact || done.current === leadId) return;
    done.current = leadId;
    let live = true;
    setState({ status: 'loading', items: [] });
    (async () => {
      try {
        const d = await crmGql(FIND_BY_CONTACT_QUERY, { value: contact.value, kind: contact.kind });
        const others = otherRequesters(d?.crmFindByContact, customerId).slice(0, 5);
        const withLeads = await Promise.all(others.map(async (customer) => {
          const l = await crmGql(CUSTOMER_DEMO_LEADS_QUERY, { customerId: customer.id, ...(intent ? { intent } : {}) }).catch(() => null);
          return { customer, leads: l?.crmLeads?.leads || [] };
        }));
        if (live) setState({ status: 'ready', items: withLeads });
      } catch {
        if (live) setState({ status: 'error', items: [] });
      }
    })();
    return () => { live = false; };
  }, [leadId, customerId, contact, intent]);

  return state;
}

function DrawerSkeleton() {
  const { t } = useTranslation();
  return (
    <SkeletonRegion label={t('demoRequests.drawer.loading')} data-testid="demo-drawer-loading" className="flex flex-col gap-6">
      <SkeletonText width="45%" height={18} />
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex flex-col gap-3">
          <SkeletonText width="25%" height={12} />
          <div className="grid grid-cols-2 gap-4">
            <Skeleton height={32} />
            <Skeleton height={32} />
            <Skeleton height={32} />
            <Skeleton height={32} />
          </div>
        </div>
      ))}
    </SkeletonRegion>
  );
}

/** Mobile/landline as a tel: link + copy; e-mail as mailto: + copy. Both LTR islands. */
function ContactRow({ point, testId }) {
  const { t } = useTranslation();
  const isEmail = point.kind === 'email';
  const display = isEmail ? point.value : (phone.forDisplay(point.value) || point.value);
  const href = isEmail ? `mailto:${point.value}` : `tel:${point.value}`;
  const Icon = isEmail ? Mail : Phone;
  return (
    <li className="flex items-center gap-2" data-testid={testId}>
      <Icon size={15} aria-hidden className="shrink-0 text-muted-foreground" />
      <a href={href} {...LTR_TEXT} className="font-semibold text-primary tabular-nums hover:underline">
        {display}
      </a>
      {point.isPrimary ? <Badge variant="outline">{t('demoRequests.drawer.primary')}</Badge> : null}
      <CopyButton
        value={point.value}
        label={isEmail ? t('demoRequests.drawer.copyEmail') : t('demoRequests.drawer.copyPhone')}
        data-testid={`${testId}-copy`}
      />
    </li>
  );
}

function TimelineItem({
  entry, mySub, closed, onComplete, completing,
}) {
  const { t, formatDateTime } = useTranslation();
  const vocab = useVocab();
  const { kind, item } = entry;
  const at = entry.atMs ? formatDateTime(entry.atMs) : EMPTY;

  if (kind === 'interaction') {
    const Icon = CHANNEL_ICON[item.channel] || PhoneCall;
    const minutes = item.durationSeconds ? Math.round(item.durationSeconds / 60) : null;
    return (
      <li className="flex gap-3" data-testid={`demo-timeline-${entry.id}`} data-kind="interaction">
        <Icon size={16} aria-hidden className="mt-0.5 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            <span className="font-semibold">{vocab(channelKey(item.channel), item.channel)}</span>
            {item.outcome ? <Badge variant="outline">{vocab(outcomeKey(item.outcome), item.outcome)}</Badge> : null}
            {minutes ? <span className="text-xs text-muted-foreground">{t('demoRequests.span.minutes', { count: minutes })}</span> : null}
          </div>
          {item.body ? <p dir="auto" className="mt-1 whitespace-pre-line text-sm text-foreground">{item.body}</p> : null}
          <div className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
            <span>{at}</span><span aria-hidden>·</span><OwnerLabel ownerId={item.actorId} mySub={mySub} />
          </div>
        </div>
      </li>
    );
  }

  if (kind === 'followUp') {
    const overdue = isOverdue(item);
    const statusTone = overdue ? 'destructive' : item.status === 'done' ? 'success' : item.status === 'open' ? 'warning' : 'outline';
    const statusLabel = overdue
      ? t('demoRequests.followUp.overdue')
      : vocab(`demoRequests.followUp.status.${item.status}`, item.status);
    return (
      <li className="flex gap-3" data-testid={`demo-timeline-${entry.id}`} data-kind="followUp">
        <CalendarClock size={16} aria-hidden className="mt-0.5 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            <span dir="auto" className="font-semibold">{item.title}</span>
            <Badge variant={statusTone} data-testid={`demo-followup-status-${item.id}`}>{statusLabel}</Badge>
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {t('demoRequests.followUp.dueAt', { when: at })}
          </div>
          {item.note ? <p dir="auto" className="mt-1 whitespace-pre-line text-sm">{item.note}</p> : null}
          {item.outcomeNote ? <p dir="auto" className="mt-1 whitespace-pre-line text-sm text-muted-foreground">{item.outcomeNote}</p> : null}
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span>{t('demoRequests.followUp.assignee')}</span>
            <OwnerLabel ownerId={item.assignedTo} mySub={mySub} />
            {item.status === 'open' && !closed ? (
              <Button
                variant="outline"
                size="sm"
                className="ms-auto"
                onClick={() => onComplete(item)}
                disabled={completing}
                aria-busy={completing || undefined}
                data-testid={`demo-followup-done-${item.id}`}
              >
                {completing ? <Spinner size={14} thickness={2} color="currentColor" /> : <CircleCheck size={14} aria-hidden />}
                {t('demoRequests.followUp.markDone')}
              </Button>
            ) : null}
          </div>
        </div>
      </li>
    );
  }

  return (
    <li className="flex gap-3" data-testid={`demo-timeline-${entry.id}`} data-kind="note">
      <StickyNote size={16} aria-hidden className="mt-0.5 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <p dir="auto" className="whitespace-pre-line text-sm">{item.body}</p>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
          <span>{at}</span><span aria-hidden>·</span><OwnerLabel ownerId={item.actorId} mySub={mySub} />
        </div>
      </div>
    </li>
  );
}

export default function DemoRequestDrawer({
  leadId, mySub, onClose, onChanged, onOpenLead,
}) {
  const { t, formatDateTime } = useTranslation();
  const toast = useToast();
  const vocab = useVocab();
  const detail = useLeadDetail(leadId);

  // Which overlay is open above the drawer: 'log' | 'followUp' | 'lost' | {move}.
  const [overlay, setOverlay] = React.useState(null);
  const [busy, setBusy] = React.useState(null);          // which inline action is in flight
  const [moveError, setMoveError] = React.useState(null);
  const [noteDraft, setNoteDraft] = React.useState('');

  // Esc belongs to the TOP overlay: while a dialog is open the drawer must not
  // close under it (the kit Drawer listens for Escape itself, so its onClose is
  // made inert too — the only place that decides is here).
  const closeDrawer = React.useCallback(() => { if (!overlay) onClose(); }, [overlay, onClose]);
  useShortcut('esc', closeDrawer, {
    label: t('shortcuts.action.closeDrawer'),
    group: t('shortcuts.groups.demoRequests'),
    enabled: !overlay,
  });

  // Refetch on focus: the owner is last-write-wins between reps.
  const { reload } = detail;
  React.useEffect(() => {
    const onFocus = () => reload();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const lead = detail.status === 'ready' ? detail.lead : null;
  const contacts = React.useMemo(() => contactsOf(detail.customer), [detail.customer]);
  const firstPhone = contacts.phones[0] || null;
  const sameNumber = useSameNumber(leadId, lead?.customerId, firstPhone, lead?.intent);

  const afterWrite = React.useCallback(() => {
    detail.reload();
    onChanged?.();
    announceDemoRequestsChanged();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail.reload, onChanged]);

  /** A drawer-level action (not inside a dialog): failure is a toast (DESIGN_CONCEPT §7). */
  const act = async (key, fn, success) => {
    if (busy) return;
    setBusy(key);
    try {
      await fn();
      toast.success(success);
      afterWrite();
    } catch (e) {
      toast.error(crmErrorMessage(e, t));
    } finally {
      setBusy(null);
    }
  };

  const takeOwnership = () => act('own', () => crmGql(UPDATE_LEAD, { leadId, input: { ownerId: mySub } }), t('demoRequests.owner.taken'));
  const release = () => act('own', () => crmGql(UPDATE_LEAD, { leadId, input: { ownerId: '' } }), t('demoRequests.owner.released'));
  const completeFollowUp = (f) => act(`fu-${f.id}`, () => crmGql(SET_FOLLOW_UP_STATUS, { followUpId: f.id, status: 'done', outcomeNote: null }), t('demoRequests.followUp.doneSuccess'));
  const addNote = () => {
    const body = noteDraft.trim();
    if (!body || !lead) return;
    act('note', async () => {
      await crmGql(ADD_NOTE, { customerId: lead.customerId, body });
      setNoteDraft('');
    }, t('demoRequests.note.success'));
  };

  const confirmMove = async () => {
    const target = overlay?.move;
    if (!target || busy) return;
    setBusy('move');
    setMoveError(null);
    try {
      await crmGql(SET_LEAD_STATUS, { leadId, status: target, lostReason: null });
      toast.success(t('demoRequests.move.success', { status: vocab(leadStatusKey(target), target) }));
      setOverlay(null);
      afterWrite();
    } catch (e) {
      // The service's own wording (NOT_ALLOWED / BAD_INPUT) — e.g. a move the
      // lead's state no longer permits because someone else moved it first.
      setMoveError(crmErrorMessage(e, t));
    } finally {
      setBusy(null);
    }
  };

  const title = lead?.customerName || t('demoRequests.drawer.title');

  let body;
  if (detail.status === 'loading') {
    body = <DrawerSkeleton />;
  } else if (detail.status === 'error') {
    body = (
      <ErrorState
        title={t('demoRequests.drawer.error')}
        description={crmErrorMessage(detail.error, t)}
        onRetry={detail.retry}
        retryLabel={t('common.actions.retry')}
        data-testid="demo-drawer-error"
      />
    );
  } else if (detail.status === 'missing') {
    body = (
      <EmptyState
        icon={<SearchX size={44} strokeWidth={1.5} aria-hidden />}
        title={t('demoRequests.drawer.missing')}
        description={t('demoRequests.drawer.missingDesc')}
        data-testid="demo-drawer-missing"
      />
    );
  } else {
    const closed = isClosedStatus(lead.status);
    const details = lead.requestDetails || {};
    // A doctor-page request carries the page's facts, and the patient's
    // message travels as the lead's NOTE (never in the details).
    const pageLead = isDoctorPageLead(lead);
    const rows = pageLead ? pageRequestRows(details) : detailRows(details);
    const message = pageLead ? (lead.note || null) : details.message;
    const utm = utmPairs(details.utm);
    const landing = safeHttpUrl(details.landingUrl);
    const timeline = buildTimeline({ interactions: detail.interactions, followUps: detail.followUps, notes: detail.notes });
    const overdueCount = detail.followUps.filter((f) => isOverdue(f)).length;
    const response = firstResponseMs(lead, detail.interactions);
    const customer = detail.customer?.customer;
    const mine = !!mySub && lead.ownerId === mySub;

    body = (
      <div
        className="flex flex-col gap-6 transition-opacity"
        style={{ opacity: detail.refreshing ? 0.6 : 1 }}
        aria-busy={detail.refreshing || undefined}
        data-state="content"
        data-testid="demo-drawer-content"
      >
        {detail.refreshFailed ? (
          <Alert variant="warning" data-testid="demo-drawer-stale">{t('demoRequests.drawer.refreshFailed')}</Alert>
        ) : null}
        {customer?.doNotContact ? (
          <Alert variant="error" title={t('demoRequests.drawer.doNotContact')} data-testid="demo-drawer-dnc">
            {customer.doNotContactReason ? <span dir="auto">{customer.doNotContactReason}</span> : t('demoRequests.drawer.doNotContactHint')}
          </Alert>
        ) : null}
        {closed ? (
          <Alert variant="neutral" data-testid="demo-drawer-closed">
            {lead.status === 'lost' && lead.lostReason
              ? t('demoRequests.drawer.closedLost', { reason: lead.lostReason })
              : t('demoRequests.drawer.closed')}
          </Alert>
        ) : null}
        {overdueCount ? (
          <Alert variant="warning" data-testid="demo-drawer-overdue">{t('demoRequests.followUp.overdueAlert', { count: overdueCount })}</Alert>
        ) : null}

        {/* The owner, prominently — last write wins between reps (§3.2 T3). */}
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-muted/40 p-3" data-testid="demo-drawer-owner">
          <span className="text-xs text-muted-foreground">{t('demoRequests.owner.label')}</span>
          <span className="text-sm"><OwnerLabel ownerId={lead.ownerId} mySub={mySub} /></span>
          {!closed ? (
            <div className="ms-auto flex gap-2">
              {!mine ? (
                <Button
                  size="sm"
                  variant={lead.ownerId ? 'outline' : 'default'}
                  onClick={takeOwnership}
                  disabled={!mySub || !!busy}
                  title={!mySub ? t('demoRequests.owner.unknownMe') : undefined}
                  aria-busy={busy === 'own' || undefined}
                  data-testid="demo-drawer-take"
                >
                  {busy === 'own' ? <Spinner size={14} thickness={2} color="currentColor" /> : <UserCheck size={14} aria-hidden />}
                  {lead.ownerId ? t('demoRequests.owner.takeOver') : t('demoRequests.owner.take')}
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={release}
                  disabled={!!busy}
                  aria-busy={busy === 'own' || undefined}
                  data-testid="demo-drawer-release"
                >
                  {busy === 'own' ? <Spinner size={14} thickness={2} color="currentColor" /> : <UserMinus size={14} aria-hidden />}
                  {t('demoRequests.owner.release')}
                </Button>
              )}
            </div>
          ) : null}
        </div>

        <Section title={t('demoRequests.drawer.sections.contact')} data-testid="demo-drawer-contact">
          {contacts.phones.length || contacts.emails.length ? (
            <ul className="flex flex-col gap-1.5">
              {contacts.phones.map((p) => <ContactRow key={p.id} point={p} testId={`demo-drawer-phone-${p.id}`} />)}
              {contacts.emails.map((p) => <ContactRow key={p.id} point={p} testId={`demo-drawer-email-${p.id}`} />)}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">{t('demoRequests.drawer.noContact')}</p>
          )}
        </Section>

        <Section title={t('demoRequests.drawer.sections.request')} data-testid="demo-drawer-request">
          {rows.length || pageLead ? (
            <dl className="grid grid-cols-2 gap-4 text-sm">
              {rows.map((r) => (
                <Fact
                  key={r.field}
                  label={t(r.labelKey)}
                  value={r.valueKey
                    ? vocab(r.valueKey, r.value)
                    : r.field === 'councilNumber'
                      ? <span {...LTR_TEXT} className="font-mono">{r.value}</span>
                      : <span dir="auto">{r.value}</span>}
                  data-testid={`demo-drawer-answer-${r.field}`}
                />
              ))}
              {pageLead ? (
                <Fact
                  wide
                  label={t('demoRequests.pageLeads.answers.page')}
                  value={<PageLink lead={lead} data-testid="demo-drawer-page-link" />}
                  data-testid="demo-drawer-answer-page"
                />
              ) : null}
            </dl>
          ) : (
            <p className="text-sm text-muted-foreground">{t('demoRequests.drawer.noAnswers')}</p>
          )}
          {message ? (
            <blockquote
              dir="auto"
              className="whitespace-pre-line rounded-lg border border-border bg-muted/40 p-3 text-sm"
              aria-label={pageLead ? t('demoRequests.pageLeads.message') : undefined}
              data-testid="demo-drawer-message"
            >
              {message}
            </blockquote>
          ) : null}
        </Section>

        <Section title={t('demoRequests.drawer.sections.origin')} data-testid="demo-drawer-origin">
          <dl className="grid grid-cols-2 gap-4 text-sm">
            <Fact
              label={t('demoRequests.drawer.reference')}
              value={details.reference ? (
                <span className="inline-flex items-center gap-1">
                  <Code>{details.reference}</Code>
                  <CopyButton value={details.reference} label={t('demoRequests.drawer.copyReference')} data-testid="demo-drawer-reference-copy" />
                </span>
              ) : null}
            />
            <Fact label={t('demoRequests.drawer.receivedAt')} value={lead.createdAtMs ? formatDateTime(Number(lead.createdAtMs)) : null} />
            <Fact
              label={t('demoRequests.drawer.firstResponse')}
              // «Not replied to yet» is a claim only a NEW lead can make. A lead
              // past `new` was answered; when its time cannot be derived (no
              // contact-made interaction, and the status has moved on since
              // `contacted`) the honest value is unknown — the dash — not "never".
              value={response != null
                ? t('demoRequests.drawer.responseAfter', { span: spanLabel(response, t) })
                : lead.status === 'new' ? t('demoRequests.drawer.noResponseYet') : null}
              data-testid="demo-drawer-first-response"
            />
            {!pageLead ? (
              <Fact label={t('demoRequests.drawer.formId')} value={details.formId ? <Code>{details.formId}</Code> : null} />
            ) : null}
            {details.landingUrl ? (
              <Fact
                wide
                label={t('demoRequests.drawer.landingUrl')}
                data-testid="demo-drawer-landing"
                value={(
                  <span className="flex min-w-0 items-center gap-2">
                    {/* TEXT, then an explicit link: the URL is visitor-supplied. */}
                    <span {...LTR_TEXT} className="min-w-0 truncate font-mono text-[12px] font-normal" title={details.landingUrl}>
                      {details.landingUrl}
                    </span>
                    {landing ? (
                      <a
                        href={landing}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-primary hover:underline"
                        data-testid="demo-drawer-landing-link"
                      >
                        <ExternalLink size={13} aria-hidden />
                        {t('demoRequests.drawer.openLink')}
                      </a>
                    ) : null}
                  </span>
                )}
              />
            ) : null}
            {utm.length ? (
              <Fact
                wide
                label={t('demoRequests.drawer.utm')}
                data-testid="demo-drawer-utm"
                value={(
                  <span className="flex flex-wrap gap-1.5">
                    {utm.map(([k, v]) => (
                      <Badge key={k} variant="outline" className="font-normal">
                        <span {...LTR_TEXT} className="font-mono">{`utm_${k}=${v}`}</span>
                      </Badge>
                    ))}
                  </span>
                )}
              />
            ) : null}
          </dl>
        </Section>

        <Section title={t('demoRequests.drawer.sections.sameNumber')} data-testid="demo-drawer-same-number">
          {sameNumber.status === 'loading' ? (
            <SkeletonText width="60%" />
          ) : sameNumber.status === 'error' ? (
            <p className="text-sm text-muted-foreground">{t('demoRequests.drawer.sameNumberError')}</p>
          ) : !firstPhone ? (
            <p className="text-sm text-muted-foreground">{t('demoRequests.drawer.noContact')}</p>
          ) : sameNumber.items.length ? (
            <ul className="flex flex-col gap-2">
              {sameNumber.items.map(({ customer: other, leads }) => (
                <li key={other.id} className="rounded-lg border border-border p-2.5 text-sm" data-testid={`demo-same-${other.id}`}>
                  <div className="flex items-center gap-2">
                    <span dir="auto" className="font-semibold">{other.fullName}</span>
                    {other.createdAtMs ? <span className="text-xs text-muted-foreground">{formatDateTime(Number(other.createdAtMs))}</span> : null}
                    {other.archivedAtMs ? <Badge variant="outline">{t('demoRequests.drawer.archived')}</Badge> : null}
                  </div>
                  {leads.length ? (
                    <ul className="mt-1.5 flex flex-wrap gap-2">
                      {leads.map((l) => (
                        <li key={l.id}>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onOpenLead(l.id)}
                            data-testid={`demo-same-open-${l.id}`}
                          >
                            <ArrowUpLeft size={13} aria-hidden style={MIRROR_ICON} />
                            <Code>{l.requestDetails?.reference || l.id.slice(0, 8)}</Code>
                            <LeadStatusBadge status={l.status} />
                          </Button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="demo-drawer-same-none">{t('demoRequests.drawer.sameNumberNone')}</p>
          )}
        </Section>

        <Section title={t('demoRequests.drawer.sections.timeline')} data-testid="demo-drawer-timeline">
          {!closed ? (
            <div className="flex flex-col gap-2" data-testid="demo-note-composer">
              <Textarea
                label={t('demoRequests.note.label')}
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target?.value ?? e)}
                maxLength={NOTE_MAX}
                rows={2}
                disabled={busy === 'note'}
                data-testid="demo-note-input"
              />
              <div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={addNote}
                  disabled={!noteDraft.trim() || !!busy}
                  aria-busy={busy === 'note' || undefined}
                  data-testid="demo-note-add"
                >
                  {busy === 'note' ? <Spinner size={14} thickness={2} color="currentColor" /> : <StickyNote size={14} aria-hidden />}
                  {t('demoRequests.note.add')}
                </Button>
              </div>
            </div>
          ) : null}
          {timeline.length ? (
            <ol className="flex flex-col gap-4">
              {timeline.map((entry) => (
                <TimelineItem
                  key={entry.id}
                  entry={entry}
                  mySub={mySub}
                  closed={closed}
                  completing={busy === `fu-${entry.item.id}`}
                  onComplete={completeFollowUp}
                />
              ))}
            </ol>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="demo-drawer-timeline-empty">{t('demoRequests.timeline.empty')}</p>
          )}
        </Section>
      </div>
    );
  }

  const closed = lead ? isClosedStatus(lead.status) : true;
  const moves = lead ? offeredMoves(lead) : { advance: [], canLose: false };

  return (
    <Drawer open onClose={closeDrawer} width={640}>
      <div role="dialog" aria-modal="true" aria-label={t('demoRequests.drawer.title')} className="flex h-full min-h-0 flex-col" data-testid="demo-drawer">
        <DrawerHeader
          title={<span dir="auto" data-testid="demo-drawer-name">{title}</span>}
          sub={lead ? (
            <span className="mt-1 flex items-center gap-2">
              <LeadStatusBadge status={lead.status} data-testid="demo-drawer-status" />
              {lead.requestDetails?.reference ? <ShortRef value={lead.requestDetails.reference} /> : null}
            </span>
          ) : null}
          onClose={closeDrawer}
        />
        <DrawerBody>{body}</DrawerBody>
        {lead && !closed ? (
          <DrawerFooter>
            <div className="flex w-full flex-wrap items-center gap-2" data-testid="demo-drawer-actions">
              <Button onClick={() => setOverlay('log')} disabled={!!busy} data-testid="demo-action-log">
                <Phone size={15} aria-hidden />
                {t('demoRequests.actions.logContact')}
              </Button>
              <Button variant="outline" onClick={() => setOverlay('followUp')} disabled={!!busy} data-testid="demo-action-followup">
                <CalendarPlus size={15} aria-hidden />
                {t('demoRequests.actions.followUp')}
              </Button>
              {moves.advance.map((status) => (
                <Button
                  key={status}
                  variant="outline"
                  onClick={() => { setMoveError(null); setOverlay({ move: status }); }}
                  disabled={!!busy}
                  data-testid={`demo-action-move-${status}`}
                >
                  {t('demoRequests.actions.moveTo', { status: vocab(leadStatusKey(status), status) })}
                </Button>
              ))}
              {moves.canLose ? (
                <Button
                  variant="ghost"
                  className="ms-auto text-destructive"
                  onClick={() => setOverlay('lost')}
                  disabled={!!busy}
                  data-testid="demo-action-lost"
                >
                  <XCircle size={15} aria-hidden />
                  {t('demoRequests.actions.lost')}
                </Button>
              ) : null}
            </div>
          </DrawerFooter>
        ) : null}
      </div>

      {lead ? (
        <>
          <LogContactDialog
            open={overlay === 'log'}
            onOpenChange={(o) => { if (!o) setOverlay(null); }}
            lead={lead}
            onDone={afterWrite}
          />
          <FollowUpDialog
            open={overlay === 'followUp'}
            onOpenChange={(o) => { if (!o) setOverlay(null); }}
            lead={lead}
            mySub={mySub}
            onDone={afterWrite}
          />
          <LostDialog
            open={overlay === 'lost'}
            onOpenChange={(o) => { if (!o) setOverlay(null); }}
            lead={lead}
            onDone={afterWrite}
          />
          <ConfirmDialog
            open={!!overlay?.move}
            onOpenChange={(o) => { if (!o && busy !== 'move') setOverlay(null); }}
            title={overlay?.move ? t('demoRequests.move.title', { status: vocab(leadStatusKey(overlay.move), overlay.move) }) : ''}
            description={overlay?.move === 'won' ? t('demoRequests.move.bodyWon') : t('demoRequests.move.body')}
            confirmLabel={t('demoRequests.move.confirm')}
            cancelLabel={t('common.actions.cancel')}
            loading={busy === 'move'}
            onConfirm={confirmMove}
            data-testid="demo-move-dialog"
            confirmTestId="demo-move-dialog-confirm"
            cancelTestId="demo-move-dialog-cancel"
          >
            {moveError ? <Alert variant="error" data-testid="demo-move-dialog-error">{moveError}</Alert> : null}
          </ConfirmDialog>
        </>
      ) : null}
    </Drawer>
  );
}
